import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { pad_with_zeroes } from "../../../../network/helper";
import {
  COLOR,
  PROGRESS_STAGES,
  StatusKey,
  STATUS_CONFIG,
  STATUS_ID_MAP,
} from "../constants/statusProjects";
import { SelectedProject } from "../types";

export type ProjectProgressCardProps = {
  selectedProject: SelectedProject | null;
  progressPercentage: number;
};

const getProjectStatus = (statusId: number | undefined): StatusKey => {
  if (!statusId) return "draft";
  return STATUS_ID_MAP[statusId] ?? "active";
};

const getDisplayStatusKey = (statusKey: StatusKey): StatusKey => {
  if (statusKey === "completed" || statusKey === "pendingReview")
    return "completed";
  if (statusKey === "inProgress" || statusKey === "partiallyCompleted")
    return "inProgress";
  return "active";
};

const getCurrentStage = (percentage: number): string => {
  for (let i = PROGRESS_STAGES.length - 1; i >= 0; i--) {
    if (percentage >= PROGRESS_STAGES[i].threshold) {
      return PROGRESS_STAGES[i].label;
    }
  }
  return PROGRESS_STAGES[0].label;
};

export default function ProjectProgressCard({
  selectedProject,
  progressPercentage,
}: ProjectProgressCardProps) {
  const statusConfig = useMemo(() => {
    const statusId = Number(selectedProject?.order?.order_status_id);
    const statusKey = getProjectStatus(statusId);
    const displayStatusKey = getDisplayStatusKey(statusKey);
    return STATUS_CONFIG[displayStatusKey];
  }, [selectedProject?.order?.order_status_id]);

  const progressInfo = useMemo(
    () => ({
      percentage: Math.round(progressPercentage),
      currentStage: getCurrentStage(progressPercentage),
      isCompleted: progressPercentage >= 100,
    }),
    [progressPercentage],
  );

  const formattedOrderId = useMemo(
    () => pad_with_zeroes(String(selectedProject?.order?.order_id ?? ""), 6),
    [selectedProject?.order?.order_id],
  );

  return (
    <Box
      bg="#F5F7FA"
      borderRadius="8px"
      border="1px solid #E6ECF3"
      p="24px"
      mb="24px"
    >
      <Flex justify="center" align="center" mb="16px" columnGap="8px">
        <Heading
          fontFamily="General Sans"
          fontSize={{ md: "24px", base: "22px" }}
          lineHeight="135%"
          fontWeight="600"
          color="#000"
          textAlign="center"
        >
          Translation project - #{formattedOrderId}
        </Heading>
        <Box
          px="8px"
          py="5px"
          bg={statusConfig.bgColor}
          fontFamily="General Sans"
          borderRadius="8px"
          lineHeight="130%"
          color={statusConfig.color}
          fontSize="16px"
          fontWeight="400"
        >
          {statusConfig.label}
        </Box>
      </Flex>

      <Box bg="white" borderRadius="12px" p="18px 24px">
        <Flex align="center" mb="8px" justify="space-between">
          <Text fontSize="14px" fontWeight="600" color="#0B0E10">
            Translation progress
          </Text>
          <Text fontSize="16px" fontWeight="600" color="#0B0E10">
            {progressInfo.percentage}%
          </Text>
        </Flex>
        <Box
          h="10px"
          borderRadius="999px"
          bg="#E5E7EB"
          overflow="hidden"
          position="relative"
        >
          <Box
            h="10px"
            w={`${progressInfo.percentage}%`}
            bg={progressInfo.isCompleted ? COLOR.Completed : COLOR.Progress}
            borderRadius="999px"
            transition="width 0.3s ease, background-color 0.3s ease"
          />
        </Box>
        <Flex justify="space-between" mt="8px">
          {PROGRESS_STAGES.map((stage) => (
            <Text
              key={stage.label}
              fontSize="14px"
              color={COLOR.ProgressInactive}
              fontWeight="400"
            >
              {stage.label}
            </Text>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}
